import type { IPoiPendingImage } from '@/entities/poi'
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { resizePoiImage } from '@/features/poi/poi-image-resizer'
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE_MB, MAX_POI_IMAGES } from '@/shared/constants'
import { cn } from '@/shared/libs/cn'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/ui/dialog'
import { getFileValidationError } from '@/shared/utils/file-validation-utils'

const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024

interface PoiImageUploadProps {
  images: IPoiPendingImage[]
  onChange: (images: IPoiPendingImage[]) => void
  onFilesSelected: (files: File[]) => void
}

export function PoiImageUpload({ images, onChange, onFilesSelected }: PoiImageUploadProps) {
  const { t } = useTranslation()
  const imagesRef = useRef(images)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  useEffect(() => {
    imagesRef.current = images
  }, [images])

  // Revoke all blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => {
        if (img.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(img.previewUrl)
      })
    }
  }, [])

  function moveImage(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= images.length) return
    const next = [...images]
    const current = next[index]
    const swap = next[target]
    if (current === undefined || swap === undefined) return
    next[index] = swap
    next[target] = current
    onChange(next)
  }

  function removeImage(index: number) {
    const image = images[index]
    if (image?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(image.previewUrl)
    onChange(images.filter((_, i) => i !== index))
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        if (images.length >= MAX_POI_IMAGES) {
          showToast.error(t('poi.media.maxImagesError', { max: MAX_POI_IMAGES }))
          break
        }

        const error = getFileValidationError(file, {
          acceptedTypes: ACCEPTED_IMAGE_TYPES,
          maxSizeMB: MAX_IMAGE_SIZE_MB,
          kind: 'image',
        })
        if (error) {
          showToast.error(error)
          continue
        }

        try {
          await resizePoiImage(file)
          onFilesSelected([file])
        } catch (error) {
          showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
        }
      }
    },
    [images, onFilesSelected, t],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: Object.fromEntries(ACCEPTED_IMAGE_TYPES.map((type) => [type, []])),
    maxSize: MAX_IMAGE_SIZE_BYTES,
    onDrop,
    onDropRejected: () => showToast.error(t('poi.media.invalidImageFile')),
    disabled: images.length >= MAX_POI_IMAGES,
  })

  return (
    <div className="space-y-3">
      {images.length < MAX_POI_IMAGES && (
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-xl min-h-[132px] px-4 py-5 text-center cursor-pointer transition-colors flex flex-col items-center justify-center',
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
          )}
        >
          <input {...getInputProps()} />
          <Upload size={20} className="mx-auto mb-1.5 text-zinc-400" />
          <p className="text-sm text-zinc-500">
            {isDragActive ? t('poi.media.dropHere') : t('poi.media.uploadImage')}
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            {t('poi.media.maxImages', { max: MAX_POI_IMAGES })}
          </p>
        </div>
      )}

      {images.map((image, index) => (
        <div
          key={image.id ?? image.blobName ?? image.url ?? image.previewUrl}
          className="flex items-center gap-3 p-2 border border-zinc-200 rounded-lg bg-white"
        >
          <button
            type="button"
            className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            onClick={() => setPreviewImageUrl(image.url ?? image.previewUrl)}
          >
            <img
              src={image.url ?? image.previewUrl}
              alt=""
              className="h-20 w-20 object-cover rounded-md border border-zinc-200"
            />
          </button>
          <div className="flex-1 text-sm text-zinc-600">
            <div>
              {t('poi.media.image')} #{index + 1}
            </div>
            {image.status === 'uploading' && (
              <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                <Loader2 size={12} className="animate-spin" />
                {t('poi.media.uploadingImage')}
              </div>
            )}
            {image.status === 'error' && (
              <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                <AlertCircle size={12} />
                {image.error ?? t('poi.media.imageUploadFailed')}
              </div>
            )}
            {image.status === 'success' && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle2 size={12} />
                {t('poi.media.imageUploadSuccess')}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={index === 0}
              onClick={async () => moveImage(index, -1)}
            >
              <ChevronUp size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={index === images.length - 1}
              onClick={async () => moveImage(index, 1)}
            >
              <ChevronDown size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-500 hover:text-red-600"
              onClick={() => removeImage(index)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      ))}

      <Dialog
        open={Boolean(previewImageUrl)}
        onOpenChange={(open) => !open && setPreviewImageUrl(null)}
      >
        <DialogContent className="max-w-4xl p-2 bg-transparent border-none shadow-none">
          <DialogTitle className="sr-only">{t('poi.media.image')}</DialogTitle>
          <DialogDescription className="sr-only">{t('common.preview')}</DialogDescription>
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt={t('poi.media.image')}
              className="w-full max-h-[80vh] object-contain rounded-lg bg-black/60"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
